import { Box } from '@mui/material';
import ExoForm from '../../components/exo/ExoForm';
import * as Yup from 'yup';
import { API_POST, API_PATCH, DBRequest, useConfig } from '../../api/api';
import Feedback from '../../components/special/Feedback';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { numberToString } from '../../components/special/numberConverter';
import { round } from '../../components/special/round';

export function calculateTravelExpensePresetTotal(dataCollection) {
  const distance = Number(dataCollection.distanceKilometers) || 0;
  const pricePerKilometer = Number(dataCollection.pricePerKilometer) || 0;
  return round(distance * pricePerKilometer, 2);
}

const NewTravelExpensePreset = ({
  projectId,
  startData,
  onCreate = () => {},
  onClose = () => {},
}) => {
  const { t } = useTranslation();
  const config = useConfig();

  const currencySymbol = config?.currency?.currencySymbol ?? '';
  const defaultPricePerKilometer =
    Number(config?.['travel expense']?.travelExpenseDefaultPricePerKilometer) ||
    0.3;

  const startDataCollection = {
    pricePerKilometer: defaultPricePerKilometer,
    ...(startData || {}),
  };

  const fields = [
    {
      label: t('From'),
      type: 'text',
      key: 'fromLocation',
    },
    {
      label: t('To'),
      type: 'text',
      key: 'toLocation',
    },
    {
      label: t('Distance'),
      type: 'number',
      key: 'distanceKilometers',
      unit: 'km',
    },
    {
      label: t('Price per Kilometer'),
      type: 'number',
      key: 'pricePerKilometer',
      unit: `${currencySymbol}/km`,
    },
    {
      label: t('Duration'),
      type: 'number',
      key: 'durationHours',
      unit: 'h',
    },
    {
      label: t('Total'),
      type: 'computed',
      key: 'totalPreview',
      compute: (data) =>
        `${numberToString(calculateTravelExpensePresetTotal(data))} ${currencySymbol}`,
    },
  ];

  const validationSchema = Yup.object().shape({
    fromLocation: Yup.string()
      .trim()
      .required(t('Start location is required')),
    toLocation: Yup.string()
      .trim()
      .required(t('Destination is required')),
    distanceKilometers: Yup.number()
      .typeError(t('Distance is required'))
      .moreThan(0, t('Distance must be greater than 0'))
      .required(t('Distance is required')),
    pricePerKilometer: Yup.number()
      .typeError(t('Price per Kilometer is required'))
      .min(0, t('Price per Kilometer must be 0 or greater'))
      .required(t('Price per Kilometer is required')),
  });

  function handleSubmit(values) {
    const payload = {
      projectId,
      fromLocation: values.fromLocation || null,
      toLocation: values.toLocation || null,
      distanceKilometers: Number(values.distanceKilometers) || 0,
      durationHours: values.durationHours ? Number(values.durationHours) : null,
      pricePerKilometer: Number(values.pricePerKilometer) || 0,
      totalPrice: calculateTravelExpensePresetTotal(values),
    };

    const isUpdate = Boolean(startData?.id);

    DBRequest({
      config,
      path: isUpdate
        ? `travel-expense-presets/${startData.id}`
        : 'travel-expense-presets',
      method: isUpdate ? API_PATCH(payload) : API_POST(payload),
      onResponse: handleResponse,
      onFeedback: handleAlertOpen,
    });
  }

  function handleResponse(data) {
    onCreate(data.data);
    onClose();
  }

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  return (
    <Box className="p-4">
      <ExoForm
        fields={fields}
        startDataCollection={startDataCollection}
        noDifferences
        onSubmit={handleSubmit}
        onCancel={onClose}
        header={startData?.id ? t('Edit Destination') : t('New Destination')}
        validationSchema={validationSchema}
        className="grid grid-cols-2 gap-4"
      />
      <Feedback setState={setAlertState} state={alertState} />
    </Box>
  );
};

export default NewTravelExpensePreset;
